import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, X, Download, Share2, Loader2, BookOpen, Link as LinkIcon, Save, Trash2, Search, Grid as GridIcon, Mail, ArrowRight, CheckCircle, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Booklet, Hotspot } from '../types';
import { recordView, saveHotspots, collectEmail } from '../services/firebase';
import LinkInputModal from './LinkInputModal';
import { AppTutorial } from './AppTutorial';

// Ensure worker version matches the React-PDF version using Cloudflare CDN for faster edge caching
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Suppress known react-pdf AbortException warnings that occur during rapid page turns or unmounting
const originalConsoleError = console.error;
console.error = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('TextLayer task cancelled')) return;
    if (args[0] && args[0] instanceof Error && args[0].message && args[0].message.includes('TextLayer task cancelled')) return;
    originalConsoleError(...args);
};
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('TextLayer task cancelled')) return;
    if (args[0] && args[0] instanceof Error && args[0].message && args[0].message.includes('TextLayer task cancelled')) return;
    originalConsoleWarn(...args);
};

interface FlipbookViewerProps {
  booklet: Booklet;
  onClose: () => void;
  onShare: () => void;
  isPublic?: boolean; 
  runTutorial?: boolean;
}

// --- SEARCH RESULT TYPE ---
interface SearchResult {
    pageIndex: number;
    matchText: string;
    matchCount: number;
}

// --- LAZY GRID PAGE COMPONENT ---
interface LazyGridPageProps {
    pageNumber: number;
    onClick: () => void;
    isCurrent: boolean;
    imageSrc?: string; // New prop for pre-rendered image
    aspectRatio: number; // Dynamic aspect ratio
}

// Renders individual grid thumbnails only when they scroll into view
const LazyGridPage: React.FC<LazyGridPageProps> = ({ pageNumber, onClick, isCurrent, imageSrc, aspectRatio }) => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect(); // Once loaded, stay loaded
                }
            },
            { rootMargin: '300px' } // Start loading before it comes into view
        );
        
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div 
            ref={containerRef} 
            onClick={onClick}
            className={`cursor-pointer group flex flex-col gap-2 ${isCurrent ? 'ring-2 ring-white rounded-lg p-1' : ''}`}
        >
            <div 
                className="bg-white relative overflow-hidden rounded-sm hover:opacity-90 transition-opacity w-full shadow-sm"
                style={{ aspectRatio: aspectRatio }}
            >
                {isVisible ? (
                    imageSrc ? (
                        <img 
                            src={imageSrc} 
                            alt={`Page ${pageNumber}`} 
                            className="w-full h-full object-cover bg-white"
                            loading="lazy"
                        />
                    ) : (
                        <Page 
                            pageNumber={pageNumber} 
                            width={250} // Fixed width for thumb generation, aspect ratio handles height
                            renderTextLayer={false} 
                            renderAnnotationLayer={false}
                            className="grid-view-canvas w-full h-full shadow-sm block"
                            loading={<div className="w-full h-full bg-white/10 animate-pulse" />}
                            error={<div className="w-full h-full bg-white flex items-center justify-center text-[10px] text-red-500">Error</div>}
                        />
                    )
                ) : (
                    <div className="w-full h-full bg-white/5" />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
            </div>
            <span className="text-white/50 text-center text-xs font-mono group-hover:text-white transition-colors">{pageNumber}</span>
        </div>
    );
};

// --- TEXT NORMALIZATION HELPERS ---
// Normalizes characters for matching: strips accents, unifies curly quotes / dash variants, lowercases.
// Applied identically to indexed text AND the query so they always compare like-for-like.
const normalizeChars = (s: string) => s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')        // strip diacritics (é -> e)
    .replace(/[\u2018\u2019\u02BC]/g, "'")  // curly apostrophes -> '
    .replace(/[\u201C\u201D]/g, '"')        // curly quotes -> "
    .replace(/[\u2010-\u2015\u2212]/g, '-') // dash variants -> -
    .replace(/\u00A0/g, ' ')                // non-breaking space -> space
    .toLowerCase();

interface PageIndexEntry {
    page: number;
    clean: string;          // display text (collapsed whitespace, original casing) - snippets come from here
    norm: string;           // normalized text used for matching
    normToClean: number[];  // norm index -> clean index (keeps snippets aligned with matches)
    condensed: string;      // norm with ALL spaces removed (catches words the PDF extractor split apart)
    condToNorm: number[];   // condensed index -> norm index
}

const buildIndexEntry = (page: number, rawText: string): PageIndexEntry => {
    const clean = rawText.replace(/\s+/g, ' ').trim();

    // Build normalized text character-by-character, recording an index map back to `clean`.
    // This is what makes snippets accurate: normalization can change string length
    // (ligatures, stripped accents), so we can't reuse indices between the two strings.
    let norm = '';
    const normToClean: number[] = [];
    for (let i = 0; i < clean.length; i++) {
        const n = normalizeChars(clean[i]);
        for (let j = 0; j < n.length; j++) {
            norm += n[j];
            normToClean.push(i);
        }
    }

    // Space-free variant: PDF text extraction frequently splits words across items
    // ("Ex ample"), which makes a plain substring search miss them.
    let condensed = '';
    const condToNorm: number[] = [];
    for (let i = 0; i < norm.length; i++) {
        if (norm[i] !== ' ') {
            condensed += norm[i];
            condToNorm.push(i);
        }
    }

    return { page, clean, norm, normToClean, condensed, condToNorm };
};

// Finds ALL occurrences of needle in haystack (the old code only found the first one per page)
const findAllOccurrences = (haystack: string, needle: string): number[] => {
    const hits: number[] = [];
    if (!needle) return hits;
    let idx = haystack.indexOf(needle);
    while (idx !== -1 && hits.length < 100) {
        hits.push(idx);
        idx = haystack.indexOf(needle, idx + 1);
    }
    return hits;
};

const makeSnippet = (clean: string, start: number, len: number): string => {
    const s = Math.max(0, start - 30);
    const e = Math.min(clean.length, start + len + 50);
    let snippet = clean.substring(s, e).trim();
    if (s > 0) snippet = '…' + snippet;
    if (e < clean.length) snippet = snippet + '…';
    return snippet;
};

// --- OPTIMIZED SEARCH LOGIC ---
const usePdfTextIndex = (
    pdfDocument: any,
    shouldIndex: boolean,
    precomputed?: { page: number; text: string }[] | null
) => {
    const [index, setIndex] = useState<PageIndexEntry[]>([]);
    const [isIndexing, setIsIndexing] = useState(false);
    const [hasIndexed, setHasIndexed] = useState(false);

    // If OCR text already exists in Firestore, build the index from it instantly
    // and never touch the PDF for search at all.
    useEffect(() => {
        if (hasIndexed || !precomputed || precomputed.length === 0) return;
        const newIndex = precomputed
            .map(p => buildIndexEntry(p.page, p.text || ''))
            .sort((a, b) => a.page - b.page);
        setIndex(newIndex);
        setHasIndexed(true);
    }, [precomputed, hasIndexed]);

    useEffect(() => {
        if (!pdfDocument || !shouldIndex || hasIndexed || (precomputed && precomputed.length > 0)) return;

        let cancelled = false;

        const indexDoc = async () => {
            setIsIndexing(true);
            const numPages = pdfDocument.numPages;
            const newIndex: PageIndexEntry[] = [];

            const processPage = async (pageNum: number) => {
                try {
                    const page = await pdfDocument.getPage(pageNum);
                    const textContent = await page.getTextContent();
                    // Respect hasEOL so line breaks don't glue unrelated words together
                    let raw = '';
                    for (const item of textContent.items) {
                        raw += (item as any).str ?? '';
                        raw += (item as any).hasEOL ? '\n' : ' ';
                    }
                    newIndex.push(buildIndexEntry(pageNum - 1, raw));
                } catch (e) {
                    console.warn(`Skipped indexing page ${pageNum}`);
                }
            };

            const BATCH_SIZE = 5;
            for (let i = 1; i <= numPages; i += BATCH_SIZE) {
                if (cancelled) return;
                const batchPromises = [];
                for (let j = i; j < i + BATCH_SIZE && j <= numPages; j++) {
                    batchPromises.push(processPage(j));
                }
                await Promise.all(batchPromises);
                await new Promise(r => setTimeout(r, 0));
            }

            if (cancelled) return;
            newIndex.sort((a, b) => a.page - b.page);
            setIndex(newIndex);
            setIsIndexing(false);
            setHasIndexed(true);
        };

        indexDoc();
        return () => { cancelled = true; };
    }, [pdfDocument, shouldIndex, hasIndexed]);

    // False when the PDF is scanned images with no text layer - search is impossible then
    const hasText = useMemo(
        () => index.some(e => e.condensed.length > 0),
        [index]
    );

    const search = useCallback((query: string): SearchResult[] => {
        if (!query || query.trim().length < 2) return [];

        // Normalize the query EXACTLY like the indexed text so quotes/accents/case never cause misses
        const q = normalizeChars(query).replace(/\s+/g, ' ').trim();
        const qCondensed = q.replace(/\s+/g, '');

        const results: SearchResult[] = [];

        index.forEach(item => {
            // 1) Direct match on normalized text
            let hits = findAllOccurrences(item.norm, q);
            let snippetStart = -1;
            let snippetLen = q.length;

            if (hits.length > 0) {
                snippetStart = item.normToClean[hits[0]] ?? 0;
            } else if (qCondensed.length >= 2) {
                // 2) Fallback: whitespace-insensitive match. Catches words the PDF
                // extractor split apart ("cata logue" still matches "catalogue").
                hits = findAllOccurrences(item.condensed, qCondensed);
                if (hits.length > 0) {
                    const normIdx = item.condToNorm[hits[0]] ?? 0;
                    snippetStart = item.normToClean[normIdx] ?? 0;
                    snippetLen = qCondensed.length + Math.ceil(qCondensed.length / 4);
                }
            }

            if (hits.length > 0 && snippetStart !== -1) {
                results.push({
                    pageIndex: item.page,
                    matchText: makeSnippet(item.clean, snippetStart, snippetLen),
                    matchCount: hits.length
                });
            }
        });

        return results;
    }, [index]);

    return { search, isIndexing, hasIndexed, hasText };
};

// --- EMAIL GATE MODAL (Multi-step) ---
interface EmailGateModalProps {
    bookletTitle: string;
    onSubmit: (email: string, marketing: boolean) => void;
    onClose?: () => void;
}

const EmailGateModal: React.FC<EmailGateModalProps> = ({ bookletTitle, onSubmit, onClose }) => {
    const [step, setStep] = useState<'email' | 'marketing'>('email');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const handleEmailSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.includes('@')) return;
        setStep('marketing');
    };

    const handleMarketingChoice = (accepted: boolean) => {
        setLoading(true);
        // Ensure UI updates to loading state before processing
        setTimeout(() => {
            onSubmit(email, accepted);
            // We don't set loading false here because the parent component will unmount this modal
        }, 300);
    };

    return (
        <div className="absolute inset-0 z-[99999] bg-black/95 flex items-center justify-center p-4">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden p-8 text-center relative"
            >
                {onClose && (
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-cool hover:text-dark transition-colors rounded-full hover:bg-gray-100"
                    >
                        <X size={20} />
                    </button>
                )}
                <AnimatePresence mode="wait">
                    {step === 'email' ? (
                        <motion.div
                            key="step-email"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                        >
                            <div className="w-16 h-16 bg-dark text-white rounded-full flex items-center justify-center mx-auto mb-6">
                                <Mail size={32} />
                            </div>
                            <h2 className="font-serif text-2xl text-dark mb-2">Access Required</h2>
                            <p className="text-cool mb-6">Please enter your email address to view <strong>{bookletTitle}</strong>.</p>
                            <form onSubmit={handleEmailSubmit} className="space-y-4">
                                <input 
                                    type="email" 
                                    required 
                                    placeholder="your@email.com" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-dark outline-none transition-all text-lg"
                                />
                                <button 
                                    type="submit" 
                                    className="w-full py-3 bg-dark text-white font-bold rounded-lg hover:bg-cool transition-colors"
                                >
                                    Continue
                                </button>
                            </form>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="step-marketing"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                        >
                            <div className="w-16 h-16 bg-warm/20 text-dark rounded-full flex items-center justify-center mx-auto mb-6">
                                <Bell size={32} />
                            </div>
                            <h2 className="font-serif text-2xl text-dark mb-3">Stay Ahead of the Curve</h2>
                            <p className="text-cool mb-8 leading-relaxed text-sm">
                                Would you like to join our exclusive list? Be the first to receive updates on new collections, trends, and special offers from Esselle Retail.
                            </p>
                            
                            <div className="space-y-3">
                                <button 
                                    onClick={() => handleMarketingChoice(true)}
                                    disabled={loading}
                                    className="w-full py-3 bg-dark text-white font-bold rounded-lg hover:bg-cool transition-colors flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 className="animate-spin" /> : <CheckCircle size={18} />}
                                    Yes, keep me updated
                                </button>
                                <button 
                                    onClick={() => handleMarketingChoice(false)}
                                    disabled={loading}
                                    className="w-full py-3 bg-white border border-gray-200 text-cool font-bold rounded-lg hover:bg-gray-50 transition-colors text-sm"
                                >
                                    No thanks, just the document
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

interface HotspotOverlayProps {
    pageIndex: number;
    hotspots: Hotspot[];
    isEditMode: boolean;
    onRemoveHotspot: (id: string) => void;
    onInteractStart: (pageIndex: number, rect: {x: number, y: number, width: number, height: number}) => void;
}

const HotspotOverlay: React.FC<HotspotOverlayProps> = ({ pageIndex, hotspots, isEditMode, onRemoveHotspot, onInteractStart }) => {
    const [currentRect, setCurrentRect] = useState<{x: number, y: number, w: number, h: number} | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pageHotspots = hotspots.filter(h => h.pageIndex === pageIndex);
    
    const isEditModeRef = useRef(isEditMode);
    isEditModeRef.current = isEditMode;
    const onInteractStartRef = useRef(onInteractStart);
    onInteractStartRef.current = onInteractStart;
    const pageIndexRef = useRef(pageIndex);
    pageIndexRef.current = pageIndex;
    const onRemoveHotspotRef = useRef(onRemoveHotspot);
    onRemoveHotspotRef.current = onRemoveHotspot;

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let drawing = false;
        let startPos: {x: number, y: number} | null = null;
        let rectWidth = 0;
        let rectHeight = 0;

        const getCoords = (e: MouseEvent | TouchEvent) => {
            const rect = el.getBoundingClientRect();
            rectWidth = rect.width;
            rectHeight = rect.height;
            let clientX, clientY;
            if ('touches' in e && (e as TouchEvent).touches.length > 0) {
                clientX = (e as TouchEvent).touches[0].clientX;
                clientY = (e as TouchEvent).touches[0].clientY;
            } else if ('changedTouches' in e && (e as TouchEvent).changedTouches.length > 0) {
                clientX = (e as TouchEvent).changedTouches[0].clientX;
                clientY = (e as TouchEvent).changedTouches[0].clientY;
            } else {
                clientX = (e as MouseEvent).clientX;
                clientY = (e as MouseEvent).clientY;
            }
            return {
                x: clientX - rect.left,
                y: clientY - rect.top
            };
        };

        const onDown = (e: MouseEvent | TouchEvent) => {
            if (!isEditModeRef.current) return;
            
            const btn = (e.target as HTMLElement).closest('button');
            if (btn) {
                e.stopPropagation();
                e.preventDefault();
                const id = btn.getAttribute('data-id');
                if (id) {
                    onRemoveHotspotRef.current(id);
                }
                return;
            }
            
            e.stopPropagation();
            e.preventDefault();
            drawing = true;
            startPos = getCoords(e);
            setCurrentRect(null);
        };

        const onMove = (e: MouseEvent | TouchEvent) => {
            if (!isEditModeRef.current || !drawing || !startPos) return;
            e.stopPropagation();
            e.preventDefault();
            const pos = getCoords(e);
            const w = Math.abs(pos.x - startPos.x);
            const h = Math.abs(pos.y - startPos.y);
            setCurrentRect({
                x: Math.min(pos.x, startPos.x),
                y: Math.min(pos.y, startPos.y),
                w,
                h
            });
        };

        const onUp = (e: MouseEvent | TouchEvent) => {
            if (!isEditModeRef.current || !drawing || !startPos) return;
            e.stopPropagation();
            e.preventDefault();
            drawing = false;
            const pos = getCoords(e);
            const wPx = Math.abs(pos.x - startPos.x);
            const hPx = Math.abs(pos.y - startPos.y);
            
            if (wPx < 10 || hPx < 10) { 
                startPos = null;
                setCurrentRect(null);
                return;
            }
            
            onInteractStartRef.current(pageIndexRef.current, {
                x: (Math.min(pos.x, startPos.x) / rectWidth) * 100,
                y: (Math.min(pos.y, startPos.y) / rectHeight) * 100,
                width: (wPx / rectWidth) * 100,
                height: (hPx / rectHeight) * 100
            });
            startPos = null;
            setCurrentRect(null);
        };

        // Use capture phase to intercept events BEFORE HTMLFlipBook
        el.addEventListener('mousedown', onDown, { capture: true });
        el.addEventListener('touchstart', onDown, { capture: true, passive: false });
        el.addEventListener('pointerdown', onDown, { capture: true });
        
        window.addEventListener('mousemove', onMove, { capture: true });
        window.addEventListener('touchmove', onMove, { capture: true, passive: false });
        window.addEventListener('pointermove', onMove, { capture: true });
        
        window.addEventListener('mouseup', onUp, { capture: true });
        window.addEventListener('touchend', onUp, { capture: true });
        window.addEventListener('pointerup', onUp, { capture: true });
        window.addEventListener('pointercancel', onUp, { capture: true });

        return () => {
            el.removeEventListener('mousedown', onDown, { capture: true });
            el.removeEventListener('touchstart', onDown, { capture: true });
            el.removeEventListener('pointerdown', onDown, { capture: true });
            
            window.removeEventListener('mousemove', onMove, { capture: true });
            window.removeEventListener('touchmove', onMove, { capture: true });
            window.removeEventListener('pointermove', onMove, { capture: true });
            
            window.removeEventListener('mouseup', onUp, { capture: true });
            window.removeEventListener('touchend', onUp, { capture: true });
            window.removeEventListener('pointerup', onUp, { capture: true });
            window.removeEventListener('pointercancel', onUp, { capture: true });
        };
    }, []);

    const stopEvent = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isEditMode) return;
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
    };

    return (
        <div 
            ref={containerRef} 
            className={`absolute inset-0 z-[200] ${isEditMode ? 'cursor-crosshair pointer-events-auto' : 'pointer-events-none'}`} 
            onClick={(e) => {
                if (isEditMode) stopEvent(e);
            }}
        >
            {pageHotspots.map(h => (
                <a 
                    key={h.id} 
                    href={isEditMode ? undefined : h.url} 
                    target={isEditMode ? undefined : "_blank"} 
                    rel="noreferrer" 
                    className="absolute group pointer-events-auto" 
                    style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.width}%`, height: `${h.height}%` }} 
                    onClick={(e) => { if (isEditMode) stopEvent(e); }} 
                    onMouseDown={(e) => { if (isEditMode) stopEvent(e); }}
                >
                    <div className={`w-full h-full relative transition-all duration-300 ${isEditMode ? 'border border-red-500 bg-red-500/10' : 'bg-transparent hover:bg-black/5'}`}>
                        {!isEditMode && (<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"><span className="relative flex h-8 w-8"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#332f21] opacity-75"></span><span className="relative inline-flex rounded-full h-8 w-8 bg-[#332f21] border-2 border-white items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><div className="w-2 h-2 bg-white rounded-full"></div></span></span></div>)}
                        {isEditMode && (<button data-id={h.id} onClick={(e) => { stopEvent(e); onRemoveHotspot(h.id); }} className="absolute -top-3 -right-3 bg-red-600 text-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform z-[250]"><Trash2 size={12} className="pointer-events-none" /></button>)}
                    </div>
                </a>
            ))}
            {currentRect && (<div className="absolute border-2 border-blue-500 bg-blue-500/20 z-50" style={{ left: currentRect.x, top: currentRect.y, width: currentRect.w, height: currentRect.h }} />)}
        </div>
    );
};

function useDebouncedResize(delay: number) {
  const [size, setSize] = useState<{ width: number, height: number } | null>(null);
  useEffect(() => {
    let timeoutId: any;
    const handleResize = () => { clearTimeout(timeoutId); timeoutId = setTimeout(() => { setSize({ width: window.innerWidth, height: window.innerHeight }); }, delay); };
    handleResize(); window.addEventListener('resize', handleResize); return () => { window.removeEventListener('resize', handleResize); clearTimeout(timeoutId); };
  }, [delay]);
  return size;
}

interface PageContentProps { 
    width: number; 
    height: number; 
    pageNumber: number; 
    onPageClick?: () => void; 
    children: React.ReactNode; 
    hotspots: Hotspot[]; 
    isEditMode: boolean; 
    onInteractStart: (pageIndex: number, rect: {x: number, y: number, width: number, height: number}) => void; 
    onRemoveHotspot: (id: string) => void; 
    isSinglePage?: boolean; 
}

const PageContent = React.memo(React.forwardRef<HTMLDivElement, PageContentProps>((props, ref) => {
    // Book spine (gutter) shadow. After the cover, spreads pair as (1,2), (3,4)...
    // odd = LEFT page → spine is its RIGHT edge; even = RIGHT page → spine is its LEFT edge.
    // Width is a percentage of the page so it scales with any booklet size.
    const isRightPage = props.pageNumber % 2 === 0;

    const shadowStyle: React.CSSProperties = {
        left: isRightPage ? 0 : undefined,
        right: isRightPage ? undefined : 0,
        width: '5%',
        minWidth: '14px',
        maxWidth: '44px',
        background: isRightPage
            ? 'linear-gradient(to right, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.07) 40%, rgba(0,0,0,0) 100%)'
            : 'linear-gradient(to left, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.07) 40%, rgba(0,0,0,0) 100%)',
    };

    return (
        <div 
            ref={ref} 
            style={{ 
                width: props.width, 
                height: props.height, 
                padding: 0, 
                margin: 0, 
                backgroundColor: '#ffffff', 
                overflow: 'hidden', 
                position: 'relative', 
                cursor: props.isEditMode ? 'crosshair' : 'default', 
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)', 
            }} 
            className="group page-content-container" 
            onClick={(e) => { 
                if (props.isEditMode) {
                    e.preventDefault(); e.stopPropagation(); return;
                }
                if (props.onPageClick) { props.onPageClick(); } 
            }}
        >
               <div className="relative z-10 w-full h-full block bg-white">{props.children}</div>
               <HotspotOverlay pageIndex={props.pageNumber} hotspots={props.hotspots} isEditMode={props.isEditMode} onInteractStart={props.onInteractStart} onRemoveHotspot={props.onRemoveHotspot} />
               {!props.isSinglePage && props.pageNumber !== 0 && (
                   <div className="absolute top-0 bottom-0 pointer-events-none z-20" style={shadowStyle} />
               )}
        </div>
    );
}));
PageContent.displayName = 'PageContent';

// --- PROGRESSIVE PAGE IMAGE (blur-up: tiny preview shows instantly, sharp image fades in) ---
// When hiResSrc is provided and wantHiRes turns true (reader zooms in), the
// full-resolution image is preloaded in the background and swapped in only
// once fully loaded — so zooming upgrades sharpness with no flash or blank.
const ProgressivePageImage: React.FC<{
    src: string;
    previewSrc?: string;
    hiResSrc?: string;
    wantHiRes?: boolean;
    width: number;
    height: number;
    alt: string;
}> = ({ src, previewSrc, hiResSrc, wantHiRes = false, width, height, alt }) => {
    const [loaded, setLoaded] = useState(false);
    const [activeSrc, setActiveSrc] = useState(src);
    const imgRef = React.useRef<HTMLImageElement>(null);

    useEffect(() => {
        setActiveSrc(src);
        // If the browser already has this image (cache), the load event may
        // have fired before React attached the listener — check directly so
        // the blur never gets stuck on an already-loaded image.
        const el = imgRef.current;
        if (el && el.complete && el.naturalWidth > 0 && el.src === src) {
            setLoaded(true);
        } else {
            setLoaded(false);
        }
    }, [src]);

    useEffect(() => {
        if (!wantHiRes || !hiResSrc || activeSrc === hiResSrc) return;
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setActiveSrc(hiResSrc); };
        img.src = hiResSrc;
        return () => { cancelled = true; };
    }, [wantHiRes, hiResSrc, activeSrc]);

    return (
        <div style={{ width, height }} className="relative bg-white overflow-hidden">
            {previewSrc && !loaded && (
                <img
                    src={previewSrc}
                    alt=""
                    aria-hidden
                    style={{ width, height }}
                    className="absolute inset-0 object-contain blur-[5px] scale-[1.02]"
                />
            )}
            <img
                ref={imgRef}
                src={activeSrc}
                alt={alt}
                style={{ width, height }}
                className={`relative object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                loading="eager"
                onLoad={() => setLoaded(true)}
                onError={() => {
                    // If a rendition fails to load, fall back to the full-size image
                    if (hiResSrc && activeSrc !== hiResSrc) setActiveSrc(hiResSrc);
                }}
            />
        </div>
    );
};

const FlipbookViewer: React.FC<FlipbookViewerProps> = ({ booklet, onClose, onShare, isPublic = false, runTutorial = false }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfDimensions, setPdfDimensions] = useState<{width: number, height: number} | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadStatus, setLoadStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [currentScale, setCurrentScale] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<any>(null); 
  
  // New: Check if images are available
  const hasImages = useMemo(() => !!(booklet.pages && booklet.pages.length > 0), [booklet.pages]);

  // On small screens, flip pages using the ~900px mobile renditions when
  // available (3-4x less data); zooming swaps in the full-res image per page.
  const isSmallScreen = useMemo(
      () => typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) < 768,
      []
  );
  const displayPages = useMemo(() => {
      if (
          isSmallScreen &&
          booklet.pagesMobile &&
          booklet.pages &&
          booklet.pagesMobile.length === booklet.pages.length
      ) {
          return booklet.pagesMobile;
      }
      return booklet.pages;
  }, [isSmallScreen, booklet.pagesMobile, booklet.pages]);
  
  // UI State
  const [showGrid, setShowGrid] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isTypingPage, setIsTypingPage] = useState(false);
  const [pageInputVal, setPageInputVal] = useState("1");

  // Hotspot State
  const [hotspots, setHotspots] = useState<Hotspot[]>(booklet.hotspots || []);
  const [isEditMode, setIsEditMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  // Modal State for Link Input
  const [pendingHotspot, setPendingHotspot] = useState<{pageIndex: number, rect: any} | null>(null);

  // Gating State
  const [isGateOpen, setIsGateOpen] = useState(() => {
      if (isPublic && booklet.emailGate) {
          const stored = localStorage.getItem(`esselle_gate_${booklet.id}`);
          return !stored;
      }
      return false;
  });

  // Background Search Indexer.
  // In PDF mode the document is loaded anyway, so index eagerly in the background
  // (search feels instant when opened). In image mode the PDF only mounts when
  // search is opened, so indexing starts then.
  const { search, isIndexing, hasIndexed, hasText } = usePdfTextIndex(
      pdfDocument,
      showSearch || !hasImages,
      booklet.pageTexts
  );

  // Handle Search Input (debounced so we don't scan the whole index on every keystroke)
  useEffect(() => {
      const t = setTimeout(() => {
          setSearchResults(search(searchQuery));
      }, 150);
      return () => clearTimeout(t);
  }, [searchQuery, search]);

  // Check Email Gate
  useEffect(() => {
      if (isPublic && booklet.emailGate) {
          const stored = localStorage.getItem(`esselle_gate_${booklet.id}`);
          if (!stored) {
              setIsGateOpen(true);
          }
      }
  }, [booklet.id, booklet.emailGate, isPublic]);

  const handleGateSubmit = async (email: string, marketing: boolean) => {
      // Store in firebase (fire and forget to prevent UI lag)
      collectEmail(booklet.id, email, marketing);
      
      // Store locally to prevent showing gate again
      localStorage.setItem(`esselle_gate_${booklet.id}`, email);
      
      // Unlock UI immediately
      setIsGateOpen(false);
  };
  
  const touchStartRef = useRef<number | null>(null);
  const windowSize = useDebouncedResize(150);
  const bookRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isGateOpen) {
        const ua = navigator.userAgent;
        let device: 'mobile' | 'desktop' | 'tablet' = 'desktop';
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            device = 'tablet';
        } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
            device = 'mobile';
        }
        recordView(booklet.id, device);
    }
  }, [booklet.id, isGateOpen]);

  // IMPORTANT: If we have images, we can set numPages immediately.
  useEffect(() => {
      if (hasImages && booklet.pages) {
          setNumPages(booklet.pages.length);
          
          // Measure the ACTUAL first page to size the flipbook to the real
          // page aspect ratio. (Using coverUrl here caused mismatches — an
          // uploaded portrait cover made landscape pages render inside a
          // portrait page with white padding around the artwork.)
          const img = new Image();
          img.onload = () => {
              setPdfDimensions({ width: img.width, height: img.height });
              setLoadStatus('success');
          };
          img.onerror = () => {
              setPdfDimensions({ width: 600, height: 848 }); // Fallback A4
              setLoadStatus('success');
          };
          img.src = booklet.pages[0] || booklet.coverUrl;
      }
  }, [hasImages, booklet.pages, booklet.coverUrl]);

  const onDocumentLoadSuccess = async (pdf: any) => {
    // If we don't have images, we rely on PDF for page count
    if (!hasImages) {
        setNumPages(pdf.numPages);
    }
    setPdfDocument(pdf); 
    try {
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        setPdfDimensions({ width: viewport.width, height: viewport.height });
        setLoadStatus('success');
    } catch (e) {
        setPdfDimensions({ width: 600, height: 800 }); 
        setLoadStatus('success');
    }
  };

  const onDocumentLoadError = () => { 
      if (hasImages) {
          setLoadStatus('success');
          // Estimate dimensions from aspect ratio of standard A4 if not available
          setPdfDimensions({ width: 600, height: 848 }); 
      } else {
          setLoadStatus('error'); 
      }
  };

  const handleNextPage = useCallback(() => { 
      if (currentPage < numPages - 1) setCurrentPage(prev => prev + 1); 
  }, [currentPage, numPages]);

  const handlePrevPage = useCallback(() => { 
      if (currentPage > 0) setCurrentPage(prev => prev - 1); 
  }, [currentPage]);
  
  const handleFlipNext = useCallback(() => { if (isEditMode) return; if (bookRef.current && bookRef.current.pageFlip()) bookRef.current.pageFlip().flipNext(); }, [isEditMode]);
  const handleFlipPrev = useCallback(() => { if (isEditMode) return; if (bookRef.current && bookRef.current.pageFlip()) bookRef.current.pageFlip().flipPrev(); }, [isEditMode]);

  const handlePageClickDesktop = useCallback((index: number) => {
      if (isEditMode) return; 
      if (index === 0) { handleFlipNext(); return; }
      const isEven = index % 2 === 0;
      if (isEven) handleFlipNext(); else handleFlipPrev();
  }, [handleFlipNext, handleFlipPrev, isEditMode]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault(); 
    if (downloading) return; 
    setDownloading(true);
    
    try {
      const response = await fetch(booklet.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${booklet.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback to opening in new tab
      window.open(booklet.url, '_blank');
    } finally {
      setDownloading(false);
    }
  };
  
  const handleInteractStart = (pageIndex: number, rect: any) => { setPendingHotspot({ pageIndex, rect }); };
  const handleLinkSubmit = useCallback((url: string) => {
      if (pendingHotspot) {
        const newHotspot: Hotspot = { id: `hs_${Date.now()}_${Math.random()}`, pageIndex: pendingHotspot.pageIndex, x: pendingHotspot.rect.x, y: pendingHotspot.rect.y, width: pendingHotspot.rect.width, height: pendingHotspot.rect.height, url, title: "Product Link" };
        setHotspots(prev => [...prev, newHotspot]); 
        setUnsavedChanges(true); 
        setPendingHotspot(null);
      }
  }, [pendingHotspot]);

  const removeHotspot = useCallback((id: string) => { 
      setHotspots(prev => prev.filter(h => h.id !== id)); 
      setUnsavedChanges(true); 
  }, []);
  const saveChanges = async () => { 
    try { 
      await saveHotspots(booklet.id, hotspots); 
      booklet.hotspots = hotspots;
      setUnsavedChanges(false); 
      setIsEditMode(false); 
      alert("Links saved successfully."); 
    } catch (e) { 
      alert("Failed to save links."); 
    } 
  };

  const makeTextRenderer = useCallback(
    (textItem: any) => {
        if (!searchQuery || searchQuery.trim().length < 2) return textItem.str;
        const str = textItem.str;
        const trimmedQuery = searchQuery.trim();
        // Basic escaping to prevent regex errors
        const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Case insensitive regex - used ONLY for splitting.
        // NOTE: never call .test() with a 'g' regex in a loop - it is stateful
        // (lastIndex advances), which made every other highlight silently fail.
        const splitRegex = new RegExp(`(${escapedQuery})`, 'gi');
        
        const parts = str.split(splitRegex);
        
        // If no match, return original string
        if (parts.length === 1) return str;

        const q = trimmedQuery.toLowerCase();
        return (
            <>
                {parts.map((part: string, index: number) => 
                    part.toLowerCase() === q ? (<mark key={index}>{part}</mark>) : (part)
                )}
            </>
        );
    },
    [searchQuery]
  );

  const dims = useMemo(() => {
    if (!windowSize || !pdfDimensions) return null;
    const isMobile = windowSize.width < 768;
    const ratio = pdfDimensions.width / pdfDimensions.height;
    const usePortrait = false; 

    const availableW = windowSize.width;
    const availableH = windowSize.height - 120;

    let pageWidth, pageHeight;

    if (isMobile) {
        const spreadWidth = availableW;
        const singlePageWidth = spreadWidth / 2;
        const calculatedHeight = singlePageWidth / ratio;
        if (calculatedHeight > availableH) {
             pageHeight = availableH;
             pageWidth = pageHeight * ratio;
        } else {
             pageHeight = calculatedHeight;
             pageWidth = singlePageWidth;
        }
    } else {
        const maxHeightByWidth = availableW / (2 * ratio);
        pageHeight = Math.min(availableH, maxHeightByWidth);
        pageWidth = pageHeight * ratio;
    }

    pageWidth = Math.max(100, Math.floor(pageWidth));
    pageHeight = Math.max(100, Math.floor(pageHeight));
    
    if (pageWidth % 2 !== 0) pageWidth -= 1;
    if (pageHeight % 2 !== 0) pageHeight -= 1;

    // --- MOBILE QUALITY OPTIMIZATION ---
    // If using images, pixelRatio doesn't matter for the <img> itself as much as canvas
    const basePixelRatio = window.devicePixelRatio || 1;
    const pixelRatio = isMobile ? Math.min(basePixelRatio * 1.5, 3.0) : Math.min(basePixelRatio, 2.5);

    return { pageWidth, pageHeight, usePortrait, pixelRatio, isMobile };
  }, [windowSize, pdfDimensions]);

  const transformKey = dims ? `${dims.pageWidth}-${dims.pageHeight}-${dims.isMobile}` : 'loading';

  const flipBookProps: any = useMemo(() => ({
    width: dims?.pageWidth || 300, 
    height: dims?.pageHeight || 400, 
    size: "fixed", 
    minWidth: dims?.pageWidth || 300, 
    maxWidth: dims?.pageWidth || 300, 
    minHeight: dims?.pageHeight || 400, 
    maxHeight: dims?.pageHeight || 400, 
    maxShadowOpacity: 0.3,
    showCover: true, 
    mobileScrollSupport: false, 
    className: "shadow-2xl", 
    startPage: 0, 
    drawShadow: true, 
    flippingTime: 800, 
    usePortrait: false, 
    startZIndex: 0, 
    autoSize: false, 
    showPageCorners: false, 
    clickEventForward: !isEditMode, 
    useMouseEvents: !isEditMode, 
    disableFlipByClick: true, 
    style: { margin: '0 auto', backgroundColor: 'transparent' }
  }), [dims, isEditMode]);

  const shouldRenderPage = (pageIndex: number, current: number, total: number) => {
    // Only render visible pages + neighbor to save memory
    const range = dims?.isMobile ? 1 : 2; 
    return (pageIndex >= current - range && pageIndex <= current + range + 1);
  };

  const handlePageJump = (pageIndex: number) => {
      const pf = bookRef.current?.pageFlip?.();
      if (!pf) return;
      const target = Math.max(0, Math.min(numPages - 1, pageIndex));

      // Close overlays first, then jump on the next frame so the flip isn't
      // swallowed while the search panel / grid is unmounting.
      setShowGrid(false);
      setShowSearch(false);
      setIsTypingPage(false);

      requestAnimationFrame(() => {
          try {
              // flip() animates nicely for short hops but can silently fail on
              // long jumps in react-pageflip; turnToPage() is the reliable API
              // for jumping directly to a distant page.
              const current = typeof pf.getCurrentPageIndex === 'function' ? pf.getCurrentPageIndex() : currentPage;
              if (Math.abs(target - current) <= 2) {
                  pf.flip(target);
              } else {
                  pf.turnToPage(target);
              }
          } catch (e) {
              try { pf.turnToPage(target); } catch { /* noop */ }
          }
          setCurrentPage(target);
      });
  };
  
  const submitPageInput = (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const p = parseInt(pageInputVal, 10);
      if (!isNaN(p)) {
          handlePageJump(p - 1);
      } else {
          setIsTypingPage(false);
      }
  };

  // State for tutorial
  const [showTutorial, setShowTutorial] = useState(runTutorial);
  useEffect(() => {
      if (runTutorial) setShowTutorial(true);
  }, [runTutorial]);

  // Preload nearby pages in the background so page turns never wait
  // (6 ahead for forward reading, 2 behind for flipping back)
  useEffect(() => {
      if (!hasImages || !displayPages) return;
      for (let off = -2; off <= 6; off++) {
          if (off === 0) continue;
          const i = currentPage + off;
          if (i < 0 || i >= displayPages.length) continue;
          const img = new Image();
          img.src = displayPages[i];
          if (booklet.pagesPreview?.[i]) {
              const p = new Image();
              p.src = booklet.pagesPreview[i];
          }
      }
  }, [currentPage, hasImages, displayPages, booklet.pagesPreview]);

  // Warm ALL tiny previews shortly after opening (a few KB each), so jumping
  // anywhere — thumbnails, page jump, search results — shows an instant
  // blur-up instead of a blank page. Delayed so the first visible pages win.
  useEffect(() => {
      if (!hasImages || !booklet.pagesPreview?.length) return;
      const timer = setTimeout(() => {
          booklet.pagesPreview!.forEach((src) => {
              const p = new Image();
              p.src = src;
          });
      }, 2500);
      return () => clearTimeout(timer);
  }, [hasImages, booklet.pagesPreview]);

  const totalMatchCount = useMemo(
      () => searchResults.reduce((sum, r) => sum + r.matchCount, 0),
      [searchResults]
  );

  if (!windowSize) return null;

  return (
    <div 
        className="fixed inset-0 z-[9999] bg-[#0d0d0d] flex flex-col h-screen w-screen overflow-hidden select-none" 
        ref={containerRef}
        style={{ overscrollBehavior: 'none', touchAction: 'none' }} // PREVENT BROWSER OVERSCROLL/RUBBER-BANDING
    >
        
        {/* Tutorial Component */}
        <AppTutorial mode="viewer" run={showTutorial} onFinish={() => setShowTutorial(false)} />

        <style>{`
            .grid-view-canvas .react-pdf__Page__canvas {
                width: 100% !important;
                height: auto !important;
            }
            .react-pdf__Page {
                background-color: white !important;
            }
            .page-content-container {
                background-color: white !important;
            }
            .stf__item {
                background-color: transparent !important;
            }
        `}</style>
        
        {isGateOpen && <EmailGateModal bookletTitle={booklet.title} onSubmit={handleGateSubmit} onClose={onClose} />}

        {showGrid && (
            <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-md overflow-y-auto p-4 md:p-8 animate-in fade-in duration-200">
                <div className="max-w-7xl mx-auto pb-20">
                    <div className="flex justify-between items-center mb-6 sticky top-0 bg-black/95 py-4 z-10 border-b border-white/10">
                        <h2 className="text-white font-serif text-2xl">Page Overview</h2>
                        <button onClick={() => setShowGrid(false)} className="p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                    {/* OPTIMIZED GRID VIEW: Use Images if available, else PDF */}
                    {(() => {
                        // Calculate Grid Aspect Ratio based on loaded dimensions
                        const gridAspectRatio = pdfDimensions ? pdfDimensions.width / pdfDimensions.height : 0.707; // Default to Portrait if unknown
                        
                        return hasImages ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 items-start">
                                {Array.from(new Array(numPages), (_, index) => (
                                    <LazyGridPage 
                                        key={index}
                                        pageNumber={index + 1}
                                        onClick={() => handlePageJump(index)}
                                        isCurrent={currentPage === index}
                                        imageSrc={booklet.pagesPreview?.[index] || booklet.pagesMobile?.[index] || booklet.pages![index]}
                                        aspectRatio={gridAspectRatio}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Document file={booklet.url} loading={<div className="text-white">Loading Grid...</div>}>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 items-start">
                                    {Array.from(new Array(numPages), (_, index) => (
                                        <LazyGridPage 
                                            key={index}
                                            pageNumber={index + 1}
                                            onClick={() => handlePageJump(index)}
                                            isCurrent={currentPage === index}
                                            aspectRatio={gridAspectRatio}
                                        />
                                    ))}
                                </div>
                            </Document>
                        );
                    })()}
                </div>
            </div>
        )}

        <AnimatePresence>
            {showSearch && (
                <motion.div 
                    key="search-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowSearch(false)}
                    className="absolute inset-0 bg-black/50 z-[160] backdrop-blur-sm"
                />
            )}
            {showSearch && (
                <motion.div 
                    key="search-panel"
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="absolute top-0 left-0 bottom-0 w-full max-w-sm bg-white z-[170] shadow-2xl flex flex-col"
                >
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                            <h2 className="font-serif text-lg text-dark flex items-center gap-2">
                                <Search size={18} />
                                Find in Document
                            </h2>
                            <button onClick={() => setShowSearch(false)} className="p-2 hover:bg-gray-200 rounded-full text-cool">
                                <X size={18} />
                            </button>
                        </div>
                        
                        <div className="p-4 border-b border-gray-100">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input 
                                    autoFocus
                                    type="text" 
                                    placeholder={isIndexing ? "Indexing text..." : "Search keyword..."}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all"
                                />
                                {isIndexing && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                            </div>
                            <div className="text-xs text-cool mt-2 flex justify-between">
                                <span>
                                    {searchQuery.trim().length >= 2
                                        ? `${totalMatchCount} match${totalMatchCount === 1 ? '' : 'es'} on ${searchResults.length} page${searchResults.length === 1 ? '' : 's'}`
                                        : `${searchResults.length} results found`}
                                </span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto bg-[#f8f7f5] p-2 space-y-2">
                            {hasIndexed && !hasText ? (
                                <div className="text-center py-10 px-6 text-cool/60 text-sm leading-relaxed">
                                    This document doesn't contain selectable text — it was likely created from scanned images or exported as pictures. Text search isn't available for it.
                                </div>
                            ) : searchResults.length === 0 ? (
                                <div className="text-center py-10 px-4 text-cool/50 text-sm">
                                    {booklet.ocrStatus === 'processing'
                                        ? "Search is being prepared for this document — check back in a couple of minutes."
                                        : isIndexing ? "Indexing document text..." : searchQuery ? "No matches found." : "Enter text to search."}
                                </div>
                            ) : (
                                searchResults.map((result, idx) => (
                                    <button 
                                        key={idx}
                                        onClick={() => handlePageJump(result.pageIndex)}
                                        className="w-full text-left p-3 bg-white rounded-lg border border-gray-100 hover:border-dark hover:shadow-md transition-all group"
                                    >
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-bold uppercase text-cool bg-gray-100 px-2 py-0.5 rounded">
                                                Page {result.pageIndex + 1}
                                                {result.matchCount > 1 && ` · ${result.matchCount}×`}
                                            </span>
                                            <ArrowRight size={14} className="text-cool opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        <p className="text-sm text-dark font-serif leading-snug line-clamp-2">"{result.matchText}"</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </motion.div>
            )}
        </AnimatePresence>

        <div className="min-h-14 py-2 bg-black/80 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 md:px-6 z-[100] flex-shrink-0 transition-all duration-300">
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white/50 hover:text-white transition-colors flex-shrink-0">
                    {isPublic ? <BookOpen size={20} /> : <X size={20} />}
                </button>
                <div className="flex flex-col cursor-pointer min-w-0" onClick={onClose}>
                    <h1 className="font-serif text-xs md:text-sm font-bold text-white uppercase tracking-wider leading-none md:leading-tight whitespace-normal line-clamp-1">
                        {booklet.title}
                    </h1>
                </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-4 tour-viewer-share">
                {!isPublic && (
                    <div className="flex items-center tour-viewer-edit mr-1 md:mr-2">
                         {isEditMode ? (
                            <div className="flex items-center gap-1 md:gap-2 bg-blue-900/50 border border-blue-500/50 rounded-full px-2 py-1">
                                <span className="hidden md:inline text-[10px] font-bold text-blue-200 uppercase tracking-widest pl-2">Editing Links</span>
                                <button onClick={saveChanges} className={`flex items-center gap-1 px-3 py-1 text-[10px] font-bold text-white rounded-full transition-all uppercase tracking-widest ${unsavedChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-white/10'}`}> <Save size={10} /> Save </button>
                                <button onClick={() => { setIsEditMode(false); setHotspots(booklet.hotspots || []); setUnsavedChanges(false); }} className="px-2 py-1 text-[10px] font-bold text-white/50 hover:text-white"> <X size={12} /> </button>
                            </div>
                         ) : (
                            <button onClick={() => setIsEditMode(true)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold text-cool hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all uppercase tracking-widest"> <LinkIcon size={12} /> <span className="hidden sm:inline">Add Links</span> </button>
                         )}
                    </div>
                )}
                 <button onClick={onShare} className="flex items-center gap-2 px-4 py-1.5 text-[10px] font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all uppercase tracking-widest">
                    <Share2 size={12} /> <span className="hidden sm:inline">Share</span>
                </button>
                <button onClick={handleDownload} disabled={downloading} className="flex items-center gap-2 px-4 py-1.5 text-[10px] font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all uppercase tracking-widest">
                    {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    <span className="hidden sm:inline">{downloading ? 'Saving...' : 'Download'}</span>
                </button>
            </div>
        </div>

        {isEditMode && (<div className="bg-[#332f21] text-white text-xs py-2 text-center font-bold uppercase tracking-widest absolute top-16 left-0 right-0 z-[90] shadow-lg"> Edit Mode Active: Click & Drag on pages to create product links </div>)}

        <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
            {!isGateOpen && (
                <>
             {(loadStatus === 'loading' || !dims) && (
                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0d0d0d] z-[150]">
                     {booklet.coverUrl ? (
                         <motion.div 
                             initial={{ opacity: 0 }} animate={{ opacity: 1 }} 
                             className="relative w-full max-w-md h-[60vh] flex items-center justify-center"
                         >
                             <img
                                 src={booklet.coverUrl}
                                 alt="Loading Cover"
                                 className="w-auto h-full object-contain shadow-2xl opacity-60 blur-sm transition-all"
                             />
                             <div className="absolute inset-0 flex flex-col items-center justify-center">
                                 <Loader2 className="animate-spin text-white mb-4 drop-shadow-xl" size={48} strokeWidth={1.5} />
                                 <span className="text-[10px] md:text-xs font-bold text-white uppercase tracking-[0.3em] md:tracking-[0.4em] bg-black/60 px-4 md:px-5 py-2.5 rounded-full backdrop-blur-md border border-white/10 shadow-2xl text-center max-w-[85%] leading-relaxed">
                                     Loading High-Res Folio...
                                 </span>
                             </div>
                         </motion.div>
                     ) : (
                         <>
                             <Loader2 className="animate-spin text-white/20 mb-4" size={40} />
                             <span className="text-[10px] font-bold text-white/20 uppercase tracking-[0.5em]">Loading Folio</span>
                         </>
                     )}
                 </div>
             )}

             {/* Only load the Document if we don't have images, or if the user opens search */}
             {(!hasImages || (showSearch && !booklet.pageTexts) || pdfDocument) && (
                 <Document 
                    file={booklet.url} 
                    onLoadSuccess={onDocumentLoadSuccess} 
                    onLoadError={onDocumentLoadError} 
                    loading={<div className="text-white/30 text-xs tracking-widest uppercase">Initializing...</div>} 
                    className={`flex items-center justify-center w-full h-full ${hasImages ? 'absolute pointer-events-none opacity-0' : ''}`} // Hide if using images
                 >
                     {/* Only render Pages here if we are in PDF Fallback mode */}
                     {!hasImages && dims && loadStatus === 'success' && (
                        <TransformWrapper 
                            key={transformKey} 
                            initialScale={1} 
                            minScale={1} 
                            maxScale={4} 
                            centerOnInit={true} 
                            centerZoomedOut={true} 
                            limitToBounds={false} 
                            wheel={{ step: 0.1, disabled: isEditMode }} 
                            // CRITICAL FIX: Disable panning when at scale 1 to prevent "dragging into void" on mobile
                            panning={{ excluded: ['flipbook-interactive-area'], disabled: isEditMode || currentScale === 1 }} 
                            onTransformed={(e) => setCurrentScale(e.state.scale)} 
                        >
                            {({ zoomIn, zoomOut, resetTransform }) => (
                                <>
                                    <TransformComponent wrapperClass="!w-screen !h-full !overflow-visible" contentClass="!w-full !h-full flex items-center justify-center !overflow-visible" >
                                        <div className="flipbook-interactive-area relative flex items-center justify-center" style={{ width: '100%', height: '100%' }}>
                                            
                                            {!isEditMode && currentScale === 1 && (
                                                <>
                                                    <div className="absolute left-0 top-0 bottom-0 w-[15%] z-20 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleFlipPrev(); }} />
                                                    <div className="absolute right-0 top-0 bottom-0 w-[15%] z-20 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleFlipNext(); }} />
                                                </>
                                            )}

                                            {numPages > 0 && (
                                                <HTMLFlipBook {...flipBookProps} ref={bookRef} onFlip={(e: any) => setCurrentPage(e.data)} >
                                                    {Array.from(new Array(numPages), (el, index) => (
                                                        <PageContent key={`page_${index}`} width={dims.pageWidth} height={dims.pageHeight} pageNumber={index} onPageClick={() => handlePageClickDesktop(index)} hotspots={hotspots} isEditMode={isEditMode} onInteractStart={handleInteractStart} onRemoveHotspot={removeHotspot} isSinglePage={dims.usePortrait}>
                                                            {shouldRenderPage(index, currentPage, numPages) ? (
                                                                <Page 
                                                                    key={`pdf_page_${index}_${dims.pageWidth}`} 
                                                                    pageNumber={index + 1} 
                                                                    width={dims.pageWidth} 
                                                                    height={dims.pageHeight} 
                                                                    renderTextLayer={true} 
                                                                    customTextRenderer={makeTextRenderer}
                                                                    renderAnnotationLayer={false} 
                                                                    loading={<div className="w-full h-full bg-white flex items-center justify-center"><Loader2 size={20} className="text-gray-200 animate-spin" /></div>} 
                                                                    error="Page Error" 
                                                                    className="bg-white relative" 
                                                                    devicePixelRatio={dims.pixelRatio} 
                                                                />
                                                            ) : ( <div className="w-full h-full bg-white flex items-center justify-center"> <div className="w-8 h-8 rounded-full border-2 border-gray-50" /> </div> )}
                                                        </PageContent>
                                                    ))}
                                                </HTMLFlipBook>
                                            )}
                                        </div>
                                    </TransformComponent>
                                    {/* Nav bar is shared below */}
                                </>
                            )}
                        </TransformWrapper>
                     )}
                </Document>
             )}

            {/* HIGH PERFORMANCE IMAGE MODE */}
            {hasImages && dims && loadStatus === 'success' && (
                <TransformWrapper 
                    key={transformKey + '_img'} 
                    initialScale={1} 
                    minScale={1} 
                    maxScale={4} 
                    centerOnInit={true} 
                    centerZoomedOut={true} 
                    limitToBounds={false} 
                    wheel={{ step: 0.1, disabled: isEditMode }} 
                    panning={{ excluded: ['flipbook-interactive-area'], disabled: isEditMode || currentScale === 1 }} 
                    onTransformed={(e) => setCurrentScale(e.state.scale)} 
                >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                            <TransformComponent wrapperClass="!w-screen !h-full !overflow-visible" contentClass="!w-full !h-full flex items-center justify-center !overflow-visible" >
                                <div className="flipbook-interactive-area relative flex items-center justify-center" style={{ width: '100%', height: '100%' }}>
                                    
                                    {!isEditMode && currentScale === 1 && (
                                        <>
                                            <div className="absolute left-0 top-0 bottom-0 w-[15%] z-20 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleFlipPrev(); }} />
                                            <div className="absolute right-0 top-0 bottom-0 w-[15%] z-20 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleFlipNext(); }} />
                                        </>
                                    )}

                                    {numPages > 0 && (
                                        <HTMLFlipBook {...flipBookProps} ref={bookRef} onFlip={(e: any) => setCurrentPage(e.data)} >
                                            {Array.from(new Array(numPages), (el, index) => (
                                                <PageContent key={`img_page_${index}`} width={dims.pageWidth} height={dims.pageHeight} pageNumber={index} onPageClick={() => handlePageClickDesktop(index)} hotspots={hotspots} isEditMode={isEditMode} onInteractStart={handleInteractStart} onRemoveHotspot={removeHotspot} isSinglePage={dims.usePortrait}>
                                                    {shouldRenderPage(index, currentPage, numPages) ? (
                                                        <ProgressivePageImage
                                                            src={displayPages![index]}
                                                            previewSrc={booklet.pagesPreview?.[index]}
                                                            hiResSrc={booklet.pages![index]}
                                                            wantHiRes={currentScale > 1.05}
                                                            width={dims.pageWidth}
                                                            height={dims.pageHeight}
                                                            alt={`Page ${index + 1}`}
                                                        />
                                                    ) : ( <div className="w-full h-full bg-white flex items-center justify-center"> <div className="w-8 h-8 rounded-full border-2 border-gray-50" /> </div> )}
                                                </PageContent>
                                            ))}
                                        </HTMLFlipBook>
                                    )}
                                </div>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            )}

            {/* SHARED NAV BAR */}
            {dims && loadStatus === 'success' && !isEditMode && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[150] bg-black/80 backdrop-blur-md text-white rounded-full px-4 py-2 flex items-center gap-2 md:gap-4 shadow-2xl border border-white/10 transition-all duration-300 w-[90%] md:w-auto max-w-xl justify-between md:justify-center tour-viewer-nav">
                    
                    <button onClick={() => setShowGrid(!showGrid)} className="p-2 hover:bg-white/20 rounded-full transition-colors tour-viewer-grid" title="Grid View">
                        <GridIcon size={18} />
                    </button>

                    <div className="w-px h-6 bg-white/20 mx-1 hidden md:block" />

                    <div className="flex items-center gap-2">
                        <button onClick={handleFlipPrev} disabled={currentPage === 0} className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30">
                            <ChevronLeft size={20} />
                        </button>
                        
                        {isTypingPage ? (
                            <form onSubmit={submitPageInput}>
                                <input 
                                    autoFocus
                                    type="number"
                                    className="w-12 bg-white/20 text-white text-center rounded text-base font-mono py-1 focus:outline-none focus:ring-1 focus:ring-white"
                                    value={pageInputVal}
                                    onChange={(e) => setPageInputVal(e.target.value)}
                                    onBlur={() => submitPageInput()}
                                />
                            </form>
                        ) : (
                            <span 
                                className="font-mono text-sm min-w-[3ch] text-center cursor-pointer hover:text-white/80 hover:underline"
                                onClick={() => {
                                    setPageInputVal((currentPage + 1).toString());
                                    setIsTypingPage(true);
                                }}
                            >
                                {currentPage + 1}
                            </span>
                        )}
                        
                        <span className="text-white/40 text-xs">/ {numPages}</span>
                        
                        <button onClick={handleFlipNext} disabled={currentPage >= numPages - 1} className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30">
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="w-px h-6 bg-white/20 mx-1 hidden md:block" />

                    <div className="hidden md:flex items-center gap-1">
                        <span className="text-xs text-white/30 px-2">Use Scroll/Pinch to Zoom</span>
                    </div>

                    <div className="w-px h-6 bg-white/20 mx-1 hidden md:block" />

                    <button onClick={() => setShowSearch(!showSearch)} className={`p-2 rounded-full transition-colors ${showSearch || searchQuery ? 'bg-white text-dark' : 'hover:bg-white/20'} relative tour-viewer-search`} title="Search Text">
                        <Search size={18} />
                        {!showSearch && searchQuery && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border border-black animate-pulse" />
                        )}
                    </button>
                </div>
            )}
                                
            {!showSearch && searchQuery && (
                <button 
                    onClick={() => setSearchQuery('')}
                    className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[140] bg-white text-dark px-4 py-2 rounded-full shadow-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4"
                >
                    Highlighting: "{searchQuery}" <X size={14} className="opacity-50 hover:opacity-100" />
                </button>
            )}
            
            </>
            )}
        </div>

        <LinkInputModal isOpen={!!pendingHotspot} onClose={() => setPendingHotspot(null)} onSubmit={handleLinkSubmit} />
    </div>
  );
};

export default FlipbookViewer;
